package songwritersnotebook.app.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_logo_fg

@Composable
fun WebFooter(
    onNavigateToHome: () -> Unit = {},
    onNavigateToFeatures: () -> Unit = {},
    onNavigateToPricing: () -> Unit = {},
    onNavigateToRoadmap: () -> Unit = {},
    onNavigateToPrivacy: () -> Unit = {},
    onNavigateToTerms: () -> Unit = {},
    onOpenUrl: (String) -> Unit = {},
    isCompact: Boolean = false
) {
    if (isCompact) {
        CompactFooter(onNavigateToPrivacy, onNavigateToTerms)
    } else {
        LargeFooter(
            onNavigateToHome,
            onNavigateToFeatures,
            onNavigateToPricing,
            onNavigateToRoadmap,
            onNavigateToPrivacy,
            onNavigateToTerms,
            onOpenUrl
        )
    }
}

@Composable
private fun LargeFooter(
    onNavigateToHome: () -> Unit,
    onNavigateToFeatures: () -> Unit,
    onNavigateToPricing: () -> Unit,
    onNavigateToRoadmap: () -> Unit,
    onNavigateToPrivacy: () -> Unit,
    onNavigateToTerms: () -> Unit,
    onOpenUrl: (String) -> Unit
) {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
    ) {
        val isMobile = maxWidth < 600.dp
        
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 40.dp, bottom = 20.dp, start = 24.dp, end = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            if (!isMobile) {
                // Desktop Footer Row
                Row(
                    modifier = Modifier.fillMaxWidth(0.8f),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top
                ) {
                    // Brand Column
                    Column(modifier = Modifier.weight(1.5f)) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Image(
                                painter = painterResource(Res.drawable.ic_logo_fg),
                                contentDescription = null,
                                modifier = Modifier.size(44.dp),
                                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.primary)
                            )
                            Spacer(Modifier.width(12.dp))
                            Text(
                                "Songwriter's Notebook",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Black,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                        }
                        Spacer(Modifier.height(12.dp))
                        Text(
                            "The professional workspace for creative teams and solo artists to write, record, and collaborate.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            lineHeight = 22.sp,
                            modifier = Modifier.fillMaxWidth(0.8f)
                        )
                    }

                    // Product Column
                    FooterColumn(
                        title = "Product",
                        items = listOf(
                            "Home" to onNavigateToHome,
                            "Features" to onNavigateToFeatures,
                            "Pricing" to onNavigateToPricing,
                            "Roadmap" to onNavigateToRoadmap
                        )
                    )

                    // Support Column
                    FooterColumn(
                        title = "Support",
                        items = listOf(
                            "Contact Us" to { onOpenUrl("mailto:support@songwritersnotebook.app") }
                        )
                    )

                    // Social Column
                    FooterColumn(
                        title = "Social",
                        items = listOf(
                            "Instagram" to { onOpenUrl("https://www.instagram.com/songwritersnotebookapp") },
                            "YouTube" to { /* Placeholder */ }
                        )
                    )
                }
            } else {
                // Mobile Footer Column
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.Start,
                    verticalArrangement = Arrangement.spacedBy(32.dp)
                ) {
                    // Brand
                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Image(
                                painter = painterResource(Res.drawable.ic_logo_fg),
                                contentDescription = null,
                                modifier = Modifier.size(36.dp),
                                colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.primary)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                "Songwriter's Notebook",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Black
                            )
                        }
                        Spacer(Modifier.height(8.dp))
                        Text(
                            "Write, record, and collaborate.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        FooterColumn(
                            title = "Product",
                            items = listOf(
                                "Home" to onNavigateToHome,
                                "Features" to onNavigateToFeatures,
                                "Pricing" to onNavigateToPricing,
                                "Roadmap" to onNavigateToRoadmap
                            ),
                            modifier = Modifier.weight(1f)
                        )
                        FooterColumn(
                            title = "Support",
                            items = listOf(
                                "Contact" to { onOpenUrl("mailto:support@songwritersnotebook.app") }
                            ),
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(40.dp))
            HorizontalDivider(modifier = Modifier.fillMaxWidth(if (isMobile) 1f else 0.8f), color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            Spacer(modifier = Modifier.height(20.dp))

            // Bottom Row
            if (!isMobile) {
                Row(
                    modifier = Modifier.fillMaxWidth(0.8f),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "© 2026 Breakwater Labs LLC. All rights reserved.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.outline
                    )
                    
                    Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                        Text(
                            text = "Privacy Policy",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.clickable { onNavigateToPrivacy() }
                        )
                        Text(
                            text = "Terms of Service",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.clickable { onNavigateToTerms() }
                        )
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                        Text(
                            text = "Privacy",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.clickable { onNavigateToPrivacy() }
                        )
                        Text(
                            text = "Terms",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.clickable { onNavigateToTerms() }
                        )
                    }
                    Text(
                        text = "© 2026 Breakwater Labs LLC",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.outline
                    )
                }
            }
        }
    }
}

@Composable
private fun CompactFooter(
    onNavigateToPrivacy: () -> Unit,
    onNavigateToTerms: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 16.dp, horizontal = 24.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "© 2026 Breakwater Labs LLC",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.outline
        )
        Spacer(modifier = Modifier.width(16.dp))
        Text(
            text = "Privacy Policy",
            style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold),
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.clickable { onNavigateToPrivacy() }
        )
        Spacer(modifier = Modifier.width(12.dp))
        Text(
            text = "Terms of Service",
            style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold),
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.clickable { onNavigateToTerms() }
        )
    }
}

@Composable
private fun FooterColumn(
    title: String,
    items: List<Pair<String, () -> Unit>>,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.widthIn(min = 100.dp)) {
        Text(
            text = title,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface
        )
        Spacer(Modifier.height(16.dp))
        items.forEach { (label, onClick) ->
            Text(
                text = label,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier
                    .clickable { onClick() }
                    .padding(vertical = 6.dp)
            )
        }
    }
}

